// E22-t2 (slice 4) — THE ORG'S SCHEDULED REPORTS.   route: /:org/reports
//
// Contract: vault/Apps/Forge/Pages/Reports.md — every report across the org's
//   knowledge bases, states included (armed | paused | unarmed | invalid |
//   orphaned — the last two are the whole reason this screen exists: a
//   definition the envelope refuses and an arm whose document is gone are
//   FACTS the org must see, with the server's own sentence beside them).
// Contract: vault/Flows/Scheduled Reports.md — f2: consent verbs land in the
//   control plane and never touch the document; the org-workspace design's
//   create/run-now controls are ABSENT rather than stubbed (creation is a
//   vault write through the KB, run-now is unbuilt — no doors to shut walls).
// Contract: the adopted org-workspace design's reports screen — lifecycle
//   table with state badges, next run, last-run dot and per-report history.
//
// Shaped like Billing.tsx: one list per KB, fan-out with per-KB degrade — an
// unreachable KB costs its own rows and a visible sentence, never the screen.
import { useEffect, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Link } from '@astryxdesign/core/Link';
import { Spinner } from '@astryxdesign/core/Spinner';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';

import { scheduleLabel } from '../../../platform/src/report-schedule';
// NOT the design system's Timestamp: the canary package's Timestamp.js imports
// its Tooltip by an extensionless path node's ESM loader refuses, so the
// component cannot LOAD under the test runner. isoDay is the app's own
// locale-pinned day formatter; relative phrasing can return with the pinned
// package upgrade.
import type { KbsClient } from '../api/kbs';
import type { ReportRow, ReportRunRow, ReportsClient, ReportState } from '../api/reports';
import { Chip, type ChipState } from '../components/Chip';
import { Eyebrow } from '../components/Eyebrow';
import { messageOf } from '../format/errors';
import { isoDay } from '../format/units';
import type { Role } from '../shell/AppShell';

/** One KB's report list, or the reason it never came — Billing's cell rule. */
type KbCell =
  | { kb: string; phase: 'ready'; reports: ReportRow[] }
  | { kb: string; phase: 'error'; message: string };

/** The state → chip vocabulary. `armed` is the healthy word; everything else
 *  is a reason to look, which is what the palette says. */
export const STATE_CHIP: Record<ReportState, ChipState> = {
  armed: 'built',
  paused: 'unspecified',
  unarmed: 'unspecified',
  invalid: 'missing',
  orphaned: 'missing',
};

const RUN_BADGE: Record<ReportRunRow['outcome'], 'success' | 'warning' | 'error' | 'neutral'> = {
  completed: 'success',
  completed_with_warnings: 'warning',
  failed: 'error',
  running: 'neutral',
};

export interface ReportsPageProps {
  org: string;
  /** the viewer's role IN THIS ORG — govern (admin) is what the consent verbs
   *  need, so the buttons are absent for everyone else: hiding is honesty,
   *  the Worker refuses regardless */
  role: Role;
  kbs: KbsClient;
  reports: ReportsClient;
}

export function ReportsPage({ org, role, kbs, reports }: ReportsPageProps) {
  const [cells, setCells] = useState<KbCell[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** report uuid → its loaded history, toggled open */
  const [history, setHistory] = useState<Record<string, ReportRunRow[] | 'loading'>>({});
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setCells(null);
    setRefusal(null);
    void (async () => {
      try {
        const rows = (await kbs.listKbs()).filter((kb) => kb.org === org);
        const settled = await Promise.all(
          rows.map(async (kb): Promise<KbCell> => {
            try {
              const found = await reports.listReports(kb.org, kb.name);
              return { kb: kb.name, phase: 'ready', reports: found.reports };
            } catch (error) {
              return { kb: kb.name, phase: 'error', message: messageOf(error) };
            }
          }),
        );
        if (live) setCells(settled);
      } catch (error) {
        if (live) setRefusal(messageOf(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [kbs, reports, org, reload]);

  if (refusal !== null) {
    return (
      <Stack direction="vertical" gap={3} data-page="reports">
        <Heading level={1}>Reports</Heading>
        <Text type="body" color="secondary">
          {refusal}
        </Text>
      </Stack>
    );
  }
  if (cells === null) {
    return (
      <Stack direction="vertical" gap={3} data-page="reports">
        <Spinner label="Reading reports" />
      </Stack>
    );
  }

  const govern = role === 'admin';

  /** One consent verb, then a reload — the DO recomputed its alarm, so the
   *  row's next-due is stale the moment the POST lands. */
  function consent(kb: string, uuid: string, verb: 'arm' | 'pause' | 'disarm') {
    setBusy(`${kb}/${uuid}`);
    reports.setConsent(org, kb, uuid, verb).then(
      () => {
        setBusy(null);
        setReload((count) => count + 1);
      },
      (error: unknown) => {
        setBusy(null);
        setRefusal(messageOf(error));
      },
    );
  }

  function toggleHistory(kb: string, uuid: string) {
    if (history[uuid] !== undefined) {
      setHistory((current) => {
        const next = { ...current };
        delete next[uuid];
        return next;
      });
      return;
    }
    setHistory((current) => ({ ...current, [uuid]: 'loading' }));
    reports.runsFor(org, kb, uuid).then(
      (runs) => setHistory((current) => ({ ...current, [uuid]: runs })),
      () => setHistory((current) => ({ ...current, [uuid]: [] })),
    );
  }

  const total = cells.reduce(
    (sum, cell) => sum + (cell.phase === 'ready' ? cell.reports.length : 0),
    0,
  );

  return (
    <Stack direction="vertical" gap={5} data-page="reports">
      <Stack direction="vertical" gap={1}>
        <Heading level={1}>Reports</Heading>
        <Text type="body" color="secondary">
          Every scheduled report across {org} — the definition lives in its knowledge base, the
          consent to run it lives here.
        </Text>
      </Stack>

      {total === 0 && cells.every((cell) => cell.phase === 'ready') ? (
        <Card padding={5} elevation="none">
          <Stack direction="vertical" gap={2}>
            <Text type="body">No knowledge base declares a report yet.</Text>
            <Text type="supporting" color="secondary">
              A report is a document with <code>type: report</code> and a schedule in its
              frontmatter, pushed like any other write — once one exists it appears here, unarmed,
              waiting for consent.
            </Text>
          </Stack>
        </Card>
      ) : null}

      {cells.map((cell) => (
        <Stack key={cell.kb} direction="vertical" gap={2} data-kb-reports={cell.kb}>
          <Eyebrow>{cell.kb}</Eyebrow>
          {cell.phase === 'error' ? (
            <Text type="supporting" color="secondary">
              reports unknown — {cell.message}
            </Text>
          ) : cell.reports.length === 0 ? (
            <Text type="supporting" color="secondary">
              no reports declared
            </Text>
          ) : (
            cell.reports.map((report) => (
              <Card key={report.uuid} padding={3} elevation="none" data-report={report.uuid}>
                <Stack direction="vertical" gap={2}>
                  <Stack direction="horizontal" gap={3} vAlign="center" wrap="wrap">
                    <Text type="label" weight="semibold">
                      {report.title}
                    </Text>
                    <Chip state={STATE_CHIP[report.state]}>{report.state}</Chip>
                    {report.schedule ? (
                      <Text type="code" color="secondary">
                        {scheduleLabel(report.schedule)}
                      </Text>
                    ) : null}
                    {report.nextDue ? (
                      <Text type="supporting" color="secondary">
                        next {isoDay(report.nextDue)}
                      </Text>
                    ) : null}
                    <Stack direction="horizontal" gap={2} vAlign="center" className="dj-actor">
                      {govern && (report.state === 'unarmed' || report.state === 'paused') ? (
                        <Button
                          type="button"
                          variant="secondary"
                          label={report.state === 'paused' ? 'Resume' : 'Arm'}
                          isDisabled={busy === `${cell.kb}/${report.uuid}`}
                          onClick={() => consent(cell.kb, report.uuid, 'arm')}
                        />
                      ) : null}
                      {govern && report.state === 'armed' ? (
                        <Button
                          type="button"
                          variant="ghost"
                          label="Pause"
                          isDisabled={busy === `${cell.kb}/${report.uuid}`}
                          onClick={() => consent(cell.kb, report.uuid, 'pause')}
                        />
                      ) : null}
                      {govern && (report.state === 'armed' || report.state === 'paused' || report.state === 'orphaned') ? (
                        <Button
                          type="button"
                          variant="ghost"
                          label="Disarm"
                          isDisabled={busy === `${cell.kb}/${report.uuid}`}
                          onClick={() => consent(cell.kb, report.uuid, 'disarm')}
                        />
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        label={history[report.uuid] === undefined ? 'History' : 'Hide history'}
                        onClick={() => toggleHistory(cell.kb, report.uuid)}
                      />
                    </Stack>
                  </Stack>

                  {report.invalidReason ? (
                    <Text type="supporting" color="secondary">
                      {report.invalidReason}
                    </Text>
                  ) : null}

                  {report.lastRun ? (
                    <Stack direction="horizontal" gap={2} vAlign="center" wrap="wrap">
                      <Badge
                        label={report.lastRun.outcome.replaceAll('_', ' ')}
                        variant={RUN_BADGE[report.lastRun.outcome]}
                      />
                      <Text type="supporting" color="secondary">
                        {isoDay(report.lastRun.startedAt)}
                      </Text>
                      {report.lastRun.reason !== '' ? (
                        <Text type="supporting" color="secondary">
                          {report.lastRun.reason}
                        </Text>
                      ) : null}
                    </Stack>
                  ) : null}

                  {history[report.uuid] === 'loading' ? (
                    <Spinner label="Reading run history" />
                  ) : Array.isArray(history[report.uuid]) ? (
                    <Stack direction="vertical" gap={1} data-run-history={report.uuid}>
                      {(history[report.uuid] as ReportRunRow[]).length === 0 ? (
                        <Text type="supporting" color="secondary">
                          no runs yet
                        </Text>
                      ) : (
                        (history[report.uuid] as ReportRunRow[]).map((run) => (
                          <Stack key={run.id} direction="horizontal" gap={2} vAlign="center" wrap="wrap">
                            <Badge
                              label={run.outcome.replaceAll('_', ' ')}
                              variant={RUN_BADGE[run.outcome]}
                            />
                            <Text type="supporting" color="secondary">
                              {isoDay(run.startedAt)}
                            </Text>
                            {run.commitOid ? (
                              <Text type="code" color="secondary">
                                {run.commitOid.slice(0, 7)}
                              </Text>
                            ) : null}
                            {run.snapshotKey ? (
                              // the landed snapshot, served to members from
                              // the same keys the lander wrote (E22-t2);
                              // wider sharing is f10's q4.
                              <>
                                <Link
                                  href={`/api/${encodeURIComponent(org)}/${encodeURIComponent(cell.kb)}/reports/${encodeURIComponent(report.uuid)}/runs/${encodeURIComponent(run.id)}/source`}
                                  type="label"
                                  isStandalone
                                >
                                  snapshot
                                </Link>
                                <Link
                                  href={`/api/${encodeURIComponent(org)}/${encodeURIComponent(cell.kb)}/reports/${encodeURIComponent(report.uuid)}/runs/${encodeURIComponent(run.id)}/manifest`}
                                  type="label"
                                  color="secondary"
                                  isStandalone
                                >
                                  manifest
                                </Link>
                              </>
                            ) : null}
                            {run.reason !== '' ? (
                              <Text type="supporting" color="secondary">
                                {run.reason}
                              </Text>
                            ) : null}
                          </Stack>
                        ))
                      )}
                    </Stack>
                  ) : null}
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export default ReportsPage;
